import { extractSubscriptionFromUserPayload } from '../services/subscriptionService';
import { mergeUserWithOrganizations } from './organizations';

/**
 * Whether the account still needs signup email verification.
 * @param {object|null|undefined} source User object or API auth payload
 */
export function needsEmailVerification(source) {
  if (!source) return false;

  if (source.verification_required === true) return true;
  if (source.verification_required === false) return false;
  if (source.status === 'pending_verification') return true;
  if (source.user_verified === 'no') return true;
  if (source.user_verified === 'yes') return false;

  const user = source.user || source;
  if (user.email_verified_at) return false;
  if (user.user_verified === 'yes') return false;
  if (user.user_verified === 'no') return true;

  return false;
}

/**
 * Whether the account must complete invited password setup.
 * @param {object|null|undefined} source User object or API auth payload
 */
export function needsPasswordChange(source) {
  if (!source) return false;

  if (source.must_change_password === true) return true;
  if (source.must_change_password === false) return false;
  if (source.code === 'password_change_required') return true;

  const user = source.user || source;
  if (user?.must_change_password === true) return true;
  if (user?.must_change_password === false) return false;

  return false;
}

/**
 * @param {object} data API auth payload (login, signup, verify)
 * @param {{ fallbackEmail?: string }} [options] Email from login form when API omits user
 */
export function normalizeAuthResponse(data, options = {}) {
  if (!data || typeof data !== 'object') {
    return {
      token: null,
      user: null,
      verificationRequired: false,
      message: null,
      otpSent: false,
    };
  }

  const { fallbackEmail } = options;
  const token = data.access_token || data.token || data.data?.token || null;
  const verificationRequired = needsEmailVerification(data);
  const mustChangePassword = needsPasswordChange(data);

  let user = data.user || data.data?.user || null;

  if (!user && (verificationRequired || data.user_verified === 'no')) {
    const email = data.email || fallbackEmail || null;
    user = {
      email,
      user_verified: data.user_verified || 'no',
      verification_required: true,
    };
  }

  if (user && verificationRequired && user.user_verified == null) {
    user = { ...user, user_verified: data.user_verified || 'no', verification_required: true };
  }

  if (user) {
    user = mergeUserWithOrganizations(
      user,
      data.organizations ?? data.user?.organizations ?? user.organizations
    );
  }

  const subscription = extractSubscriptionFromUserPayload(data);

  return {
    token,
    user,
    verificationRequired,
    subscription,
    message: data.message || null,
    warning: data.warning || null,
    verificationExpiresAt: data.verification_expires_at || null,
    otpExpiresInMinutes: data.otp_expires_in_minutes ?? null,
    otpSent: data.otp_sent === true,
    mustChangePassword,
    code: data.code || null,
  };
}
